// Geocoding service for converting location names to coordinates
// Uses Mapbox Geocoding API (primary), falls back to Nominatim if no key configured

import config from '@/config';

export interface GeocodingResult {
  lat: string;
  lon: string;
  display_name: string;
  name?: string;
  address?: {
    city?: string;
    town?: string;
    village?: string;
    state?: string;
    country?: string;
    postcode?: string;
  };
  boundingbox: string[];
  type: string;
  importance: number;
}

// Mapbox feature from their geocoding API
interface MapboxFeature {
  place_name: string;
  text: string;
  center: [number, number]; // [lon, lat]
  relevance: number;
  bbox?: [number, number, number, number];
  context?: Array<{ id: string; text: string }>;
  properties?: { category?: string };
}

class GeocodingService {
  private readonly mapboxKey = config.services.mapboxApiKey;
  private readonly nominatimUrl = config.services.nominatimApiUrl;

  private get useMapbox(): boolean {
    return !!this.mapboxKey && !this.mapboxKey.startsWith('__');
  }

  // Convert Mapbox feature to our standard GeocodingResult format
  private mapboxToResult(feature: MapboxFeature): GeocodingResult {
    const context = feature.context || [];
    const getContext = (prefix: string) => context.find(c => c.id.startsWith(prefix))?.text;

    return {
      lat: feature.center[1].toString(),
      lon: feature.center[0].toString(),
      display_name: feature.place_name,
      name: feature.text,
      address: {
        city: getContext('place'),
        state: getContext('region'),
        country: getContext('country'),
        postcode: getContext('postcode'),
      },
      boundingbox: feature.bbox
        ? [feature.bbox[1].toString(), feature.bbox[3].toString(), feature.bbox[0].toString(), feature.bbox[2].toString()]
        : [],
      type: feature.properties?.category || 'place',
      importance: feature.relevance,
    };
  }

  // Mapbox returns exact place/address matches with relevance ~1.0, but for
  // fuzzy POI queries (e.g. "Whistler clinic") it has no POI match and returns
  // low-relevance junk — it mis-parses "clinic" as the abbreviation "Cl" and
  // matches a road "Cl-500" in Crofton (rel 0.5). Below this threshold we
  // cross-check Nominatim, which handles named POIs/clinics/hospitals well.
  private readonly mapboxConfidenceThreshold = 0.8;

  // Search for a location by name
  async search(query: string, options?: {
    countryCode?: string;
    limit?: number;
    viewbox?: { minLat: number; minLon: number; maxLat: number; maxLon: number };
  }): Promise<GeocodingResult[]> {
    if (!this.useMapbox) {
      return this.searchNominatim(query, options);
    }

    const mapboxResults = await this.searchMapbox(query, options);
    // GeocodingResult.importance carries the Mapbox relevance score.
    const topRelevance = mapboxResults[0]?.importance ?? 0;
    if (topRelevance < this.mapboxConfidenceThreshold) {
      // Low confidence — prefer Nominatim if it finds anything; otherwise keep
      // Mapbox's best guess (which is often the right area, just imprecise).
      const nominatimResults = await this.searchNominatim(query, options);
      if (nominatimResults.length > 0) {
        return nominatimResults;
      }
    }
    return mapboxResults;
  }

  private async searchMapbox(query: string, options?: {
    countryCode?: string;
    limit?: number;
    viewbox?: { minLat: number; minLon: number; maxLat: number; maxLon: number };
  }): Promise<GeocodingResult[]> {
    try {
      const params = new URLSearchParams({
        access_token: this.mapboxKey,
        limit: (options?.limit || 5).toString(),
        types: 'poi,address,place,locality,neighborhood',
      });

      if (options?.countryCode) {
        params.append('country', options.countryCode);
      }

      // Bias results toward BC center
      const proximity = `${config.map.defaultCenter.lng},${config.map.defaultCenter.lat}`;
      params.append('proximity', proximity);

      if (options?.viewbox) {
        const { minLat, minLon, maxLat, maxLon } = options.viewbox;
        params.append('bbox', `${minLon},${minLat},${maxLon},${maxLat}`);
      }

      const encodedQuery = encodeURIComponent(query);
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodedQuery}.json?${params}`,
        { signal: AbortSignal.timeout(config.timeouts.geocoding) }
      );

      if (!response.ok) {
        console.error('Mapbox geocoding error:', response.status);
        // Fall back to Nominatim
        return this.searchNominatim(query, options);
      }

      const data = await response.json();
      return (data.features || []).map((f: MapboxFeature) => this.mapboxToResult(f));
    } catch (error) {
      console.error('Mapbox geocoding failed, falling back to Nominatim:', error);
      return this.searchNominatim(query, options);
    }
  }

  private async searchNominatim(query: string, options?: {
    countryCode?: string;
    limit?: number;
    viewbox?: { minLat: number; minLon: number; maxLat: number; maxLon: number };
  }): Promise<GeocodingResult[]> {
    try {
      const params = new URLSearchParams({
        q: query,
        format: 'json',
        addressdetails: '1',
        limit: (options?.limit || 5).toString()
      });

      if (options?.countryCode) {
        params.append('countrycodes', options.countryCode);
      }

      if (options?.viewbox) {
        const { minLat, minLon, maxLat, maxLon } = options.viewbox;
        params.append('viewbox', `${minLon},${minLat},${maxLon},${maxLat}`);
        params.append('bounded', '1'); // Hard-bound to BC for dispatch system
      }

      const response = await fetch(`${this.nominatimUrl}/search?${params}`, {
        headers: { 'User-Agent': config.app.userAgent },
        signal: AbortSignal.timeout(config.timeouts.geocoding)
      });

      if (!response.ok) {
        console.error('Nominatim geocoding error:', response.status);
        return [];
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to geocode location:', error);
      return [];
    }
  }

  // Reverse geocode coordinates to location name
  async reverse(lat: number, lon: number): Promise<GeocodingResult | null> {
    if (this.useMapbox) {
      return this.reverseMapbox(lat, lon);
    }
    return this.reverseNominatim(lat, lon);
  }

  private async reverseMapbox(lat: number, lon: number): Promise<GeocodingResult | null> {
    try {
      const params = new URLSearchParams({
        access_token: this.mapboxKey,
        types: 'poi,address,place',
      });

      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${lon},${lat}.json?${params}`,
        { signal: AbortSignal.timeout(config.timeouts.geocoding) }
      );

      if (!response.ok) {
        return this.reverseNominatim(lat, lon);
      }

      const data = await response.json();
      if (data.features && data.features.length > 0) {
        return this.mapboxToResult(data.features[0]);
      }
      return null;
    } catch (error) {
      console.error('Mapbox reverse geocoding failed:', error);
      return this.reverseNominatim(lat, lon);
    }
  }

  private async reverseNominatim(lat: number, lon: number): Promise<GeocodingResult | null> {
    try {
      const params = new URLSearchParams({
        lat: lat.toString(),
        lon: lon.toString(),
        format: 'json',
        addressdetails: '1'
      });

      const response = await fetch(`${this.nominatimUrl}/reverse?${params}`, {
        headers: { 'User-Agent': config.app.userAgent },
        signal: AbortSignal.timeout(config.timeouts.geocoding)
      });

      if (!response.ok) {
        return null;
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to reverse geocode:', error);
      return null;
    }
  }

  // Format a geocoding result for display
  formatResult(result: GeocodingResult): string {
    if (result.name && result.address) {
      const parts = [result.name];

      if (result.address.city || result.address.town || result.address.village) {
        parts.push((result.address.city || result.address.town || result.address.village)!);
      }

      if (result.address.state) {
        parts.push(result.address.state);
      }

      return parts.join(', ');
    }

    return result.display_name;
  }

  // Search specifically for locations in BC, Canada
  async searchBC(query: string): Promise<GeocodingResult[]> {
    return this.search(query, {
      countryCode: 'ca',
      viewbox: {
        minLat: 48.0,
        minLon: -139.0,
        maxLat: 60.0,
        maxLon: -114.0
      }
    });
  }
}

export default new GeocodingService();
